import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const LoaderContext = createContext();

export const useLoader = () => {
  const context = useContext(LoaderContext);
  if (!context) {
    throw new Error('useLoader must be used within a LoaderProvider');
  }
  return context;
};

export const LoaderProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [title, setTitle] = useState('Loading');
  const [subText, setSubText] = useState('');
  const hideTimeoutRef = useRef(null);
  const showTimeRef = useRef(Date.now());

  const showLoader = useCallback((options = {}) => {
    // Clear any existing hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const { title = 'Loading', subText = '' } = options;
    setTitle(title);
    setSubText(subText);
    setIsLoading(true);
    setIsTransitioning(true);
    showTimeRef.current = Date.now();
  }, []);

  const hideLoader = useCallback(() => {
    const currentTime = Date.now();
    const timeElapsed = currentTime - showTimeRef.current;
    const minimumDelay = Math.max(0, 800 - timeElapsed);

    // Clear any existing hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // First set transitioning to false
    setIsTransitioning(false);

    // Then schedule the loader to hide after the minimum delay
    hideTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      hideTimeoutRef.current = null;
    }, minimumDelay);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <LoaderContext.Provider
      value={{
        isLoading,
        isTransitioning,
        title,
        subText,
        showLoader,
        hideLoader
      }}
    >
      {children}
    </LoaderContext.Provider>
  );
}; 